import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { API } from "../config";

// ─── Date validation helper ───
const isValidDate = (mm, dd, yyyy) => {
  const m = parseInt(mm, 10);
  const d = parseInt(dd, 10);
  const y = parseInt(yyyy, 10);
  if (!m || !d || !y) return false;
  if (m < 1 || m > 12) return false;
  if (y < 1000) return false;
  const daysInMonth = new Date(y, m, 0).getDate();
  if (d < 1 || d > daysInMonth) return false;
  const inputDate = new Date(y, m - 1, d);
  if (inputDate > new Date()) return false;
  return true;
};

// Date input: single real <input> that formats as MM-DD-YYYY
const DateInput = ({ id, name, required, className }) => {
  const [display, setDisplay] = useState("");
  const inputRef = useRef(null);

  const formatMMDDYYYY = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    const mm = digits.slice(0, 2);
    const dd = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);
    if (digits.length <= 2) return mm;
    if (digits.length <= 4) return `${mm}-${dd}`;
    return `${mm}-${dd}-${yyyy}`;
  };

  const handleChange = (e) => {
    const formatted = formatMMDDYYYY(e.target.value);
    setDisplay(formatted);

    // Custom validity check
    const el = inputRef.current;
    if (!el) return;
    const parts = formatted.split("-");
    if (parts.length === 3 && parts[2]?.length === 4) {
      if (!isValidDate(parts[0], parts[1], parts[2])) {
        el.setCustomValidity("Please enter a valid date (MM-DD-YYYY)");
      } else {
        el.setCustomValidity("");
      }
    } else {
      el.setCustomValidity("");
    }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      id={id}
      name={name}
      required={required}
      value={display}
      onChange={handleChange}
      placeholder="MM-DD-YYYY"
      inputMode="numeric"
      autoComplete="off"
      className={className}
      maxLength={10}
      pattern="\d{2}-\d{2}-\d{4}"
      title="Please enter a valid date (MM-DD-YYYY)"
      onInvalid={(e) =>
        e.target.setCustomValidity("Please enter a valid date (MM-DD-YYYY)")
      }
    />
  );
};

// EIN input: formats as XX-XXXXXXX
const EINInput = ({ id, name, required, className }) => {
  const [display, setDisplay] = useState("");

  const formatEIN = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 9);
    const part1 = digits.slice(0, 2);
    const part2 = digits.slice(2, 9);
    if (digits.length <= 2) return part1;
    return `${part1}-${part2}`;
  };

  const handleChange = (e) => {
    setDisplay(formatEIN(e.target.value));
  };

  return (
    <input
      type="text"
      id={id}
      name={name}
      required={required}
      value={display}
      onChange={handleChange}
      placeholder="XX-XXXXXXX"
      inputMode="numeric"
      autoComplete="off"
      className={className}
      maxLength={10}
      pattern="\d{2}-\d{7}"
      title="Please enter a complete EIN (XX-XXXXXXX)"
      onInvalid={(e) =>
        e.target.setCustomValidity("Please enter a complete EIN (XX-XXXXXXX)")
      }
      onInput={(e) => e.target.setCustomValidity("")}
    />
  );
};

// SSN input: formats as XXX-XX-XXXX
const SSNInput = ({ id, name, required, className }) => {
  const [display, setDisplay] = useState("");

  const formatSSN = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 9);
    const part1 = digits.slice(0, 3);
    const part2 = digits.slice(3, 5);
    const part3 = digits.slice(5, 9);
    if (digits.length <= 3) return part1;
    if (digits.length <= 5) return `${part1}-${part2}`;
    return `${part1}-${part2}-${part3}`;
  };

  const handleChange = (e) => {
    setDisplay(formatSSN(e.target.value));
  };

  return (
    <input
      type="text"
      id={id}
      name={name}
      required={required}
      value={display}
      onChange={handleChange}
      placeholder="XXX-XX-XXXX"
      inputMode="numeric"
      autoComplete="off"
      className={className}
      maxLength={11}
      pattern="\d{3}-\d{2}-\d{4}"
      title="Please enter a complete SSN (XXX-XX-XXXX)"
      onInvalid={(e) =>
        e.target.setCustomValidity("Please enter a complete SSN (XXX-XX-XXXX)")
      }
      onInput={(e) => e.target.setCustomValidity("")}
    />
  );
};

// Phone input: formats as (XXX) XXX-XXXX
const PhoneInput = ({ id, name, required, className }) => {
  const [display, setDisplay] = useState("");

  const formatPhone = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 10);
    const area = digits.slice(0, 3);
    const prefix = digits.slice(3, 6);
    const line = digits.slice(6, 10);
    if (digits.length <= 3) return digits.length > 0 ? `(${area}` : "";
    if (digits.length <= 6) return `(${area}) ${prefix}`;
    return `(${area}) ${prefix}-${line}`;
  };

  const handleChange = (e) => {
    setDisplay(formatPhone(e.target.value));
  };

  return (
    <input
      type="text"
      id={id}
      name={name}
      required={required}
      value={display}
      onChange={handleChange}
      placeholder="(XXX) XXX-XXXX"
      inputMode="numeric"
      autoComplete="off"
      className={className}
      maxLength={14}
      pattern="\(\d{3}\) \d{3}-\d{4}"
      title="Please enter a complete phone number (XXX) XXX-XXXX"
      onInvalid={(e) =>
        e.target.setCustomValidity(
          "Please enter a complete phone number (XXX) XXX-XXXX"
        )
      }
      onInput={(e) => e.target.setCustomValidity("")}
    />
  );
};

// Numeric only input (for zip, percentage, fico)
const NumericInput = ({
  id,
  name,
  required,
  className,
  maxLength,
  placeholder,
}) => {
  const [value, setValue] = useState("");

  const handleChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, maxLength || 10);
    setValue(digits);
  };

  return (
    <input
      type="text"
      id={id}
      name={name}
      required={required}
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      inputMode="numeric"
      autoComplete="off"
      className={className}
      maxLength={maxLength}
      onInvalid={(e) => e.target.setCustomValidity("This field is required")}
      onInput={(e) => e.target.setCustomValidity("")}
    />
  );
};

const ApplicationForm = () => {
  const [hasSecondOwner, setHasSecondOwner] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const formRef = useRef(null);

  // Get lead ID from URL params (e.g., /application?lid=123456)
  const urlParams = new URLSearchParams(window.location.search);
  const leadId = urlParams.get("lid");
  const signatureRef1 = useRef(null);
  const signatureRef2 = useRef(null);
  const [signature1, setSignature1] = useState(null);
  const [signature2, setSignature2] = useState(null);
  const isDrawingRef = useRef(false);

  // Setup high-DPI canvas for sharp signatures
  const setupHiDPICanvas = (canvas) => {
    if (!canvas) return null;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();

    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);

    const ctx = canvas.getContext("2d");

    // Reset transforms then scale to DPR
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);

    ctx.imageSmoothingEnabled = true;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.miterLimit = 2;

    return ctx;
  };

  // Initialize canvases after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      if (signatureRef1.current) {
        setupHiDPICanvas(signatureRef1.current);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Initialize canvas 2 when second owner is enabled
  useEffect(() => {
    if (hasSecondOwner) {
      const timer = setTimeout(() => {
        if (signatureRef2.current) {
          setupHiDPICanvas(signatureRef2.current);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [hasSecondOwner]);

  // Signature pad handlers
  const startDrawing = (e, canvasRef) => {
    if (e.touches) e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const isTouch = !!e.touches;
    ctx.strokeStyle = "#1e3a5f";
    ctx.lineWidth = isTouch ? 3.2 : 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    ctx.beginPath();
    ctx.moveTo(x, y);
    isDrawingRef.current = true;
  };

  const draw = (e, canvasRef) => {
    if (!isDrawingRef.current) return;
    if (e.touches) e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const isTouch = !!e.touches;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = isTouch ? 3.2 : 2.5;

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = (canvasRef, setSignature) => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    const canvas = canvasRef.current;
    setSignature(canvas.toDataURL("image/png"));
  };

  const clearSignature = (canvasRef, setSignature) => {
    const canvas = canvasRef.current;
    const ctx = setupHiDPICanvas(canvas);
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    setSignature(null);
  };

  // Real-time error clearing
  const clearFieldError = (el) => {
    if (!el || !el.validity) return;
    if (el.type === "radio") {
      const groupWrapper = el.closest(".mt-2");
      if (groupWrapper && groupWrapper.hasAttribute("data-error")) {
        groupWrapper.removeAttribute("data-error");
        const msg = groupWrapper.querySelector(".field-error-msg");
        if (msg) msg.remove();
      }
      return;
    }
    if (!el.hasAttribute("data-error") || !el.validity.valid) return;
    el.removeAttribute("data-error");
    el.style.borderColor = "";
    el.style.backgroundColor = "";
    const msg = el.parentNode.querySelector(".field-error-msg");
    if (msg) msg.remove();
    if (el.type === "checkbox") {
      const wrapper = el.closest("label");
      if (wrapper) {
        wrapper.removeAttribute("data-error");
        wrapper.style.border = "";
        wrapper.style.backgroundColor = "";
        wrapper.style.padding = "";
        wrapper.style.borderRadius = "";
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);

    const form = formRef.current;

    // Clear previous error highlights and messages
    form.querySelectorAll("[data-error]").forEach((el) => {
      el.removeAttribute("data-error");
      el.style.borderColor = "";
      el.style.backgroundColor = "";
      el.style.border = "";
      el.style.padding = "";
      el.style.borderRadius = "";
    });
    form.querySelectorAll(".field-error-msg").forEach((el) => el.remove());

    if (!form.checkValidity()) {
      const invalidFields = form.querySelectorAll(
        "input:invalid, select:invalid"
      );
      const handledRadioGroups = new Set();
      invalidFields.forEach((el) => {
        if (el.type === "radio") {
          if (handledRadioGroups.has(el.name)) return;
          handledRadioGroups.add(el.name);
          const groupWrapper = el.closest(".mt-2");
          if (groupWrapper) {
            groupWrapper.setAttribute("data-error", "true");
            const msg = document.createElement("p");
            msg.className = "field-error-msg";
            msg.style.cssText = "color:#ef4444;font-size:12px;margin-top:8px;";
            msg.textContent = "Please select an entity type";
            groupWrapper.appendChild(msg);
          }
          return;
        }
        el.setAttribute("data-error", "true");
        el.style.borderColor = "#ef4444";
        el.style.backgroundColor = "#fef2f2";

        const msg = document.createElement("p");
        msg.className = "field-error-msg";
        msg.style.cssText = "color:#ef4444;font-size:12px;margin-top:4px;";

        if (el.type === "checkbox") {
          const wrapper = el.closest("label");
          if (wrapper) {
            wrapper.setAttribute("data-error", "true");
            wrapper.style.backgroundColor = "#fef2f2";
            wrapper.style.border = "1px solid #ef4444";
            wrapper.style.borderRadius = "12px";
            wrapper.style.padding = "12px";
          }
          msg.textContent = "Please check this box to proceed";
        } else {
          msg.textContent = el.validationMessage || "This field is required";
        }
        el.parentNode.appendChild(msg);
      });
      const firstInvalid = invalidFields[0];
      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
        firstInvalid.focus({ preventScroll: true });
      }
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    // Add signatures
    data.signature1 = signature1;
    data.signature2 = hasSecondOwner ? signature2 : null;
    data.hasSecondOwner = hasSecondOwner;
    data.leadId = leadId || null;

    try {
      const response = await fetch(
        API.SUBMIT_APPLICATION,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );

      if (response.ok) {
        window.location.href = "/thank-you";
      } else {
        throw new Error("Submission failed");
      }
    } catch (error) {
      alert("Oops! Something went wrong. Please try again.");
      setIsSubmitting(false);
    }
  };

  const inputClasses =
    "mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition";

  const labelClasses = "block text-sm font-medium text-slate-700";

  const sectionClasses =
    "bg-white rounded-2xl p-6 sm:p-8 shadow-sm ring-1 ring-slate-100";

  return (
    <>
      <SEO
        title="Apply for Funding"
        description="Submit your business funding application with Lloyd Funding. Quick online form — get approved in as little as 3 hours."
        canonical="/application"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-50 to-white">
        <div className="mx-auto max-w-3xl px-4 pt-20 pb-12 sm:px-6 sm:pt-24 sm:pb-12 lg:px-8 lg:pt-24 lg:pb-16">
          <div className="text-center">
            <h1 className="text-3xl font-bold tracking-tight text-slate-800 sm:text-4xl lg:text-5xl">
              Funding <span className="text-blue-700">Application</span>
            </h1>
            <p className="mt-4 text-lg text-slate-600 text-pretty">
              Complete the form below to apply for business funding. All
              information is kept secure and confidential.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="bg-slate-50 pb-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            noValidate
            onInput={(e) => clearFieldError(e.target)}
            onChange={(e) => clearFieldError(e.target)}
            className={`space-y-8 ${formSubmitted ? "form-submitted" : ""}`}
          >
            {/* Business Information */}
            <div className={sectionClasses}>
              <h2 className="text-xl font-semibold text-slate-800 mb-6">
                Business Information
              </h2>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="businessLegalName" className={labelClasses}>
                    Business Legal Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="businessLegalName"
                    name="businessLegalName"
                    required
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label htmlFor="businessDBA" className={labelClasses}>
                    Business DBA
                  </label>
                  <input
                    type="text"
                    id="businessDBA"
                    name="businessDBA"
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label htmlFor="ein" className={labelClasses}>
                    EIN/Tax ID <span className="text-red-500">*</span>
                  </label>
                  <EINInput
                    id="ein"
                    name="ein"
                    required={true}
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label htmlFor="industry" className={labelClasses}>
                    Industry <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="industry"
                    name="industry"
                    required
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label htmlFor="businessStartDate" className={labelClasses}>
                    Business Start Date <span className="text-red-500">*</span>
                  </label>
                  <DateInput
                    id="businessStartDate"
                    name="businessStartDate"
                    required={true}
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label className={labelClasses}>
                    Entity Type <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2 grid grid-cols-2 gap-3">
                    {["LLC", "Corp", "Sole Prop", "Partnership"].map((type) => (
                      <label
                        key={type}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="entityType"
                          value={type}
                          required
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300"
                        />
                        <span className="text-sm text-slate-700">{type}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="businessStreet" className={labelClasses}>
                    Business Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="businessStreet"
                    name="businessStreet"
                    required
                    placeholder="Street Address"
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label htmlFor="businessCity" className={labelClasses}>
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="businessCity"
                    name="businessCity"
                    required
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label htmlFor="businessState" className={labelClasses}>
                    State <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="businessState"
                    name="businessState"
                    required
                    className={inputClasses + " appearance-none"}
                  >
                    <option value="">Please Select</option>
                    {[
                      "AL",
                      "AK",
                      "AZ",
                      "AR",
                      "CA",
                      "CO",
                      "CT",
                      "DE",
                      "FL",
                      "GA",
                      "HI",
                      "ID",
                      "IL",
                      "IN",
                      "IA",
                      "KS",
                      "KY",
                      "LA",
                      "ME",
                      "MD",
                      "MA",
                      "MI",
                      "MN",
                      "MS",
                      "MO",
                      "MT",
                      "NE",
                      "NV",
                      "NH",
                      "NJ",
                      "NM",
                      "NY",
                      "NC",
                      "ND",
                      "OH",
                      "OK",
                      "OR",
                      "PA",
                      "RI",
                      "SC",
                      "SD",
                      "TN",
                      "TX",
                      "UT",
                      "VT",
                      "VA",
                      "WA",
                      "WV",
                      "WI",
                      "WY",
                    ].map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="businessZip" className={labelClasses}>
                    Zip Code <span className="text-red-500">*</span>
                  </label>
                  <NumericInput
                    id="businessZip"
                    name="businessZip"
                    required={true}
                    maxLength={5}
                    placeholder="XXXXX"
                    className={inputClasses}
                  />
                </div>
              </div>
            </div>

            {/* Owner's Information */}
            <div className={sectionClasses}>
              <h2 className="text-xl font-semibold text-slate-800 mb-6">
                Owner's Information
              </h2>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="owner1FirstName" className={labelClasses}>
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="owner1FirstName"
                    name="owner1FirstName"
                    required
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label htmlFor="owner1LastName" className={labelClasses}>
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="owner1LastName"
                    name="owner1LastName"
                    required
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label htmlFor="owner1SSN" className={labelClasses}>
                    Social Security Number{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <SSNInput
                    id="owner1SSN"
                    name="owner1SSN"
                    required={true}
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label htmlFor="owner1Ownership" className={labelClasses}>
                    Ownership Percentage (%){" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <NumericInput
                    id="owner1Ownership"
                    name="owner1Ownership"
                    required={true}
                    maxLength={3}
                    placeholder="%"
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label htmlFor="owner1DOB" className={labelClasses}>
                    Date of Birth <span className="text-red-500">*</span>
                  </label>
                  <DateInput
                    id="owner1DOB"
                    name="owner1DOB"
                    required={true}
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label htmlFor="owner1FICO" className={labelClasses}>
                    FICO Score
                  </label>
                  <NumericInput
                    id="owner1FICO"
                    name="owner1FICO"
                    required={false}
                    maxLength={3}
                    placeholder="e.g., 650"
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label htmlFor="owner1Phone" className={labelClasses}>
                    Cell Phone <span className="text-red-500">*</span>
                  </label>
                  <PhoneInput
                    id="owner1Phone"
                    name="owner1Phone"
                    required={true}
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label htmlFor="owner1Email" className={labelClasses}>
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="owner1Email"
                    name="owner1Email"
                    required
                    pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                    title="Please enter a valid email (e.g., name@example.com)"
                    onInvalid={(e) =>
                      e.target.setCustomValidity(
                        "Please enter a valid email (e.g., name@example.com)"
                      )
                    }
                    onInput={(e) => e.target.setCustomValidity("")}
                    className={inputClasses}
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    *Application confirmation will be sent to this email
                  </p>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="owner1Street" className={labelClasses}>
                    Home Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="owner1Street"
                    name="owner1Street"
                    required
                    placeholder="Street Address"
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label htmlFor="owner1City" className={labelClasses}>
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="owner1City"
                    name="owner1City"
                    required
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label htmlFor="owner1State" className={labelClasses}>
                    State <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="owner1State"
                    name="owner1State"
                    required
                    className={inputClasses + " appearance-none"}
                  >
                    <option value="">Please Select</option>
                    {[
                      "AL",
                      "AK",
                      "AZ",
                      "AR",
                      "CA",
                      "CO",
                      "CT",
                      "DE",
                      "FL",
                      "GA",
                      "HI",
                      "ID",
                      "IL",
                      "IN",
                      "IA",
                      "KS",
                      "KY",
                      "LA",
                      "ME",
                      "MD",
                      "MA",
                      "MI",
                      "MN",
                      "MS",
                      "MO",
                      "MT",
                      "NE",
                      "NV",
                      "NH",
                      "NJ",
                      "NM",
                      "NY",
                      "NC",
                      "ND",
                      "OH",
                      "OK",
                      "OR",
                      "PA",
                      "RI",
                      "SC",
                      "SD",
                      "TN",
                      "TX",
                      "UT",
                      "VT",
                      "VA",
                      "WA",
                      "WV",
                      "WI",
                      "WY",
                    ].map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="owner1Zip" className={labelClasses}>
                    Zip Code <span className="text-red-500">*</span>
                  </label>
                  <NumericInput
                    id="owner1Zip"
                    name="owner1Zip"
                    required={true}
                    maxLength={5}
                    placeholder="XXXXX"
                    className={inputClasses}
                  />
                </div>
              </div>
            </div>

            {/* 2nd Owner Toggle */}
            <div className={sectionClasses}>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasSecondOwner}
                  onChange={(e) => setHasSecondOwner(e.target.checked)}
                  className="h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-base font-medium text-slate-800">
                  Add 2nd Owner Information
                </span>
              </label>

              {hasSecondOwner && (
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-800 mb-5">
                    2nd Owner's Information
                  </h3>

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="owner2FirstName" className={labelClasses}>
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="owner2FirstName"
                        name="owner2FirstName"
                        required={hasSecondOwner}
                        className={inputClasses}
                      />
                    </div>

                    <div>
                      <label htmlFor="owner2LastName" className={labelClasses}>
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="owner2LastName"
                        name="owner2LastName"
                        required={hasSecondOwner}
                        className={inputClasses}
                      />
                    </div>

                    <div>
                      <label htmlFor="owner2SSN" className={labelClasses}>
                        Social Security Number{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <SSNInput
                        id="owner2SSN"
                        name="owner2SSN"
                        required={hasSecondOwner}
                        className={inputClasses}
                      />
                    </div>

                    <div>
                      <label htmlFor="owner2Ownership" className={labelClasses}>
                        Ownership Percentage (%){" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <NumericInput
                        id="owner2Ownership"
                        name="owner2Ownership"
                        required={hasSecondOwner}
                        maxLength={3}
                        placeholder="%"
                        className={inputClasses}
                      />
                    </div>

                    <div>
                      <label htmlFor="owner2DOB" className={labelClasses}>
                        Date of Birth <span className="text-red-500">*</span>
                      </label>
                      <DateInput
                        id="owner2DOB"
                        name="owner2DOB"
                        required={hasSecondOwner}
                        className={inputClasses}
                      />
                    </div>

                    <div>
                      <label htmlFor="owner2FICO" className={labelClasses}>
                        FICO Score
                      </label>
                      <NumericInput
                        id="owner2FICO"
                        name="owner2FICO"
                        required={false}
                        maxLength={3}
                        placeholder="e.g., 650"
                        className={inputClasses}
                      />
                    </div>

                    <div>
                      <label htmlFor="owner2Phone" className={labelClasses}>
                        Cell Phone
                      </label>
                      <PhoneInput
                        id="owner2Phone"
                        name="owner2Phone"
                        required={false}
                        className={inputClasses}
                      />
                    </div>

                    <div>
                      <label htmlFor="owner2Email" className={labelClasses}>
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="owner2Email"
                        name="owner2Email"
                        pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                        title="Please enter a valid email (e.g., name@example.com)"
                        onInvalid={(e) =>
                          e.target.setCustomValidity(
                            "Please enter a valid email (e.g., name@example.com)"
                          )
                        }
                        onInput={(e) => e.target.setCustomValidity("")}
                        className={inputClasses}
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label htmlFor="owner2Street" className={labelClasses}>
                        Home Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="owner2Street"
                        name="owner2Street"
                        required={hasSecondOwner}
                        placeholder="Street Address"
                        className={inputClasses}
                      />
                    </div>

                    <div>
                      <label htmlFor="owner2City" className={labelClasses}>
                        City <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="owner2City"
                        name="owner2City"
                        required={hasSecondOwner}
                        className={inputClasses}
                      />
                    </div>

                    <div>
                      <label htmlFor="owner2State" className={labelClasses}>
                        State <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="owner2State"
                        name="owner2State"
                        required={hasSecondOwner}
                        className={inputClasses + " appearance-none"}
                      >
                        <option value="">Please Select</option>
                        {[
                          "AL",
                          "AK",
                          "AZ",
                          "AR",
                          "CA",
                          "CO",
                          "CT",
                          "DE",
                          "FL",
                          "GA",
                          "HI",
                          "ID",
                          "IL",
                          "IN",
                          "IA",
                          "KS",
                          "KY",
                          "LA",
                          "ME",
                          "MD",
                          "MA",
                          "MI",
                          "MN",
                          "MS",
                          "MO",
                          "MT",
                          "NE",
                          "NV",
                          "NH",
                          "NJ",
                          "NM",
                          "NY",
                          "NC",
                          "ND",
                          "OH",
                          "OK",
                          "OR",
                          "PA",
                          "RI",
                          "SC",
                          "SD",
                          "TN",
                          "TX",
                          "UT",
                          "VT",
                          "VA",
                          "WA",
                          "WV",
                          "WI",
                          "WY",
                        ].map((state) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="owner2Zip" className={labelClasses}>
                        Zip Code <span className="text-red-500">*</span>
                      </label>
                      <NumericInput
                        id="owner2Zip"
                        name="owner2Zip"
                        required={hasSecondOwner}
                        maxLength={5}
                        placeholder="XXXXX"
                        className={inputClasses}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Signatures */}
            <div className={sectionClasses}>
              <h2 className="text-xl font-semibold text-slate-800 mb-6">
                Owner's Signature
              </h2>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className={labelClasses + " mb-2"}>
                    1. Owner's Signature <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <canvas
                      ref={signatureRef1}
                      className="w-full h-40 border border-slate-200 rounded-xl bg-white cursor-crosshair touch-none"
                      onMouseDown={(e) => startDrawing(e, signatureRef1)}
                      onMouseMove={(e) => draw(e, signatureRef1)}
                      onMouseUp={() =>
                        stopDrawing(signatureRef1, setSignature1)
                      }
                      onMouseLeave={() => (isDrawingRef.current = false)}
                      onTouchStart={(e) => startDrawing(e, signatureRef1)}
                      onTouchMove={(e) => draw(e, signatureRef1)}
                      onTouchEnd={() =>
                        stopDrawing(signatureRef1, setSignature1)
                      }
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => clearSignature(signatureRef1, setSignature1)}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                  >
                    Clear
                  </button>
                </div>

                {hasSecondOwner && (
                  <div>
                    <label className={labelClasses + " mb-2"}>
                      2. Owner's Signature
                    </label>
                    <div className="relative">
                      <canvas
                        ref={signatureRef2}
                        className="w-full h-40 border border-slate-200 rounded-xl bg-white cursor-crosshair touch-none"
                        onMouseDown={(e) => startDrawing(e, signatureRef2)}
                        onMouseMove={(e) => draw(e, signatureRef2)}
                        onMouseUp={() =>
                          stopDrawing(signatureRef2, setSignature2)
                        }
                        onMouseLeave={() => (isDrawingRef.current = false)}
                        onTouchStart={(e) => startDrawing(e, signatureRef2)}
                        onTouchMove={(e) => draw(e, signatureRef2)}
                        onTouchEnd={() =>
                          stopDrawing(signatureRef2, setSignature2)
                        }
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        clearSignature(signatureRef2, setSignature2)
                      }
                      className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                    >
                      Clear
                    </button>
                  </div>
                )}

                <div className={hasSecondOwner ? "sm:col-span-2" : ""}>
                  <label htmlFor="signatureDate" className={labelClasses}>
                    Signature Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    id="signatureDate"
                    name="signatureDate"
                    required
                    defaultValue={new Date().toISOString().split("T")[0]}
                    className={inputClasses + " sm:max-w-xs"}
                  />
                </div>
              </div>
            </div>

            {/* Legal Disclaimer */}
            <div className={sectionClasses}>
              <div className="text-xs leading-relaxed text-slate-500 mt-4">
                <p className="font-medium text-slate-600 mb-2">
                  Authorization & Consent:
                </p>
                <p>
                  By signing above, you authorize Lloyd Funding Group Inc dba
                  Lloyd Funding and its representatives, successors, assigns,
                  and designees ("Recipients") to:
                </p>
                <ol className="list-decimal pl-5 mt-2 space-y-1.5">
                  <li>
                    Obtain consumer, personal, business, and investigative
                    reports, including credit inquiries (hard or soft pulls),
                    from agencies including TransUnion, Experian, and Equifax;
                  </li>
                  <li>
                    Obtain credit card processing and bank statements from
                    financial institutions and third parties;
                  </li>
                  <li>
                    Share this application and related information with
                    Recipients for the purposes described herein;
                  </li>
                  <li>
                    Authorize any creditor or financial institution to release
                    your information to Lloyd Funding and Recipients;
                  </li>
                  <li>
                    Contact you by email, phone, and/or text message at the
                    contact information provided, including mobile numbers, even
                    if registered on a Do-Not-Call list, using automated dialing
                    systems, regarding this application, future financing
                    opportunities, and/or other lawful marketing purposes.
                  </li>
                </ol>
                <p className="mt-2">
                  Consent to receive text messages is not required for
                  application approval. Message frequency varies. Message and
                  data rates may apply. Reply STOP to opt out. I/We certify that
                  all information provided is complete, true, and accurate. View
                  our{" "}
                  <Link
                    to="/privacy-policy"
                    className="text-blue-600 hover:text-blue-700 underline"
                  >
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link
                    to="/terms-of-service"
                    className="text-blue-600 hover:text-blue-700 underline"
                  >
                    Terms of Service
                  </Link>
                  .
                </p>
              </div>

              <div className="mt-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="agreeToTerms"
                    required
                    className="mt-1 h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-700">
                    I have read and agree to the terms above{" "}
                    <span className="text-red-500">*</span>
                  </span>
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={
                  isSubmitting || !signature1 || (hasSecondOwner && !signature2)
                }
                className="w-full rounded-xl bg-blue-600 px-8 py-4 text-base font-semibold text-white shadow-sm shadow-blue-600/25 hover:bg-blue-700 hover:shadow-md hover:shadow-blue-600/30 transition-colors transition-shadow transition-[filter] duration-200 active:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  "Submit Application"
                )}
              </button>
              <p className="mt-4 text-center text-xs text-slate-500">
                Your information is encrypted and securely transmitted.
              </p>
            </div>
          </form>
        </div>
      </section>
    </>
  );
};

export default ApplicationForm;